import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetPatientDocumentsQueryKey,
  PatientDocumentCategory,
  useUploadPatientDocument,
} from '../../../api/created/documents.ts';
import { FileInput } from '../../../components/form/FileInput.tsx';
import { FormActionButtons } from '../../../components/form/FormActionButtons.tsx';
import { getDocumentCategoryName } from '../../../utils/document.ts';

interface FormValues {
  category: PatientDocumentCategory | null;
  file: File | null;
}

const defaultValues: FormValues = {
  category: null,
  file: null,
};

export function UploadDocumentDialog({
  patientId,
  categories,
  open,
  onClose,
}: {
  patientId: string;
  categories: readonly PatientDocumentCategory[];
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues,
  });

  const uploadDocumentMutation = useUploadPatientDocument({
    onSuccess: () => {
      toast.success('Dokument wurde erfolgreich hochgeladen');
      void queryClient.invalidateQueries({ queryKey: getGetPatientDocumentsQueryKey(patientId) });
      handleOnClose();
    },
    onError: () => {
      toast.error('Beim Hochladen des Dokuments ist ein Fehler aufgetreten');
    },
  });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset]);

  const handleOnClose = () => {
    reset(defaultValues);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 600 }}>
        <ModalClose />
        <Typography level={'h4'}>Dokument hochladen</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit((data) => {
            uploadDocumentMutation.mutate({
              patientId,
              file: data.file!,
              category: data.category!,
            });
          })}>
          <Stack gap={2}>
            <FormControl error={!!errors.category}>
              <FormLabel>
                <Typography>Kategorie *</Typography>
              </FormLabel>
              <Controller
                name="category"
                control={control}
                rules={{ required: 'Kategorie muss ausgewählt werden' }}
                render={({ field: { onChange, value } }) => (
                  <Select value={value} onChange={(_, newValue) => onChange(newValue)} placeholder="Bitte auswählen">
                    {categories.map((category) => (
                      <Option key={category} value={category}>
                        {getDocumentCategoryName(category)}
                      </Option>
                    ))}
                  </Select>
                )}
              />
              {errors.category && <FormHelperText>{errors.category.message}</FormHelperText>}
            </FormControl>

            <FormControl error={!!errors.file}>
              <FormLabel>
                <Typography>Datei *</Typography>
              </FormLabel>
              <Controller
                name="file"
                control={control}
                rules={{ required: 'Datei muss hochgeladen werden' }}
                render={({ field: { onChange, value } }) => (
                  <FileInput value={value} onChange={onChange} placeholder={'Datei hochladen'} />
                )}
              />
              {errors.file && <FormHelperText>{errors.file.message}</FormHelperText>}
            </FormControl>

            <FormActionButtons onAbortClick={handleOnClose} isLoading={uploadDocumentMutation.isPending} />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}
